import { ArrowRight, BookOpen, HelpCircle } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Breadcrumbs from "../components/Breadcrumbs";
import PageSEO from "../components/PageSEO";
import SiteFooter from "../components/SiteFooter";
import SiteHeader from "../components/SiteHeader";
import { getComparisonBySlug, visibleComparisons } from "../content/comparisons";

type ComparisonPageProps = { slug: string };

export default function ComparisonPage({ slug }: ComparisonPageProps) {
  const entry = getComparisonBySlug(slug);

  if (!entry) {
    return (
      <>
        <PageSEO
          title="Comparison not found | Anter"
          description="Compare Anter against Claude, OpenAI AgentKit, CrewAI, and LangGraph. Discover why our managed multi-agent orchestration platform scales where others fail."
          canonical="/compare"
          noindex
        />
        <div className="relative min-h-dvh overflow-x-hidden bg-bg font-sans text-text">
          <SiteHeader scrolled />
          <main
            id="main-content"
            tabIndex={-1}
            className="mx-auto w-full max-w-3xl px-4 pb-24 pt-28 sm:px-5 md:px-8 lg:pt-32"
          >
            <h1 className="mb-3 text-3xl font-extrabold tracking-tight text-text">
              Comparison not found
            </h1>
            <p className="mb-6 text-text-muted">This comparison is unavailable.</p>
            <a
              href="/compare"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary-text"
            >
              Browse comparisons
              <ArrowRight className="h-4 w-4" />
            </a>
          </main>
          <SiteFooter />
        </div>
      </>
    );
  }

  const canonical = `/compare/${entry.slug}`;
  const related = entry.related
    .map((relatedSlug) => visibleComparisons.find((c) => c.slug === relatedSlug))
    .filter((c): c is NonNullable<typeof c> => Boolean(c));

  // `SoftwareApplication` describes Anter itself (same shape as
  // /solutions/mcp-agent-platform); `FAQPage` covers the visible FAQ section
  // below. Google restricts FAQ rich-snippet eligibility to certain site
  // types, but the schema still helps AI answer engines parse the Q&A pairs
  // — the actual goal for a comparison spoke page like this one.
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Anter AI Agent Orchestration Platform",
      applicationCategory: "DeveloperApplication",
      operatingSystem: "Web",
      description: entry.shortDefinition,
      url: `https://anter.ai${canonical}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `https://anter.ai${canonical}#faq`,
      mainEntity: entry.faq.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    },
  ];

  return (
    <>
      <PageSEO
        title={`${entry.term} — Anter vs. ${entry.vendor}`}
        description={entry.shortDefinition}
        canonical={canonical}
        jsonLd={jsonLd}
      />

      <div className="relative min-h-dvh overflow-x-hidden bg-bg font-sans text-text">
        <SiteHeader scrolled />

        <main
          id="main-content"
          tabIndex={-1}
          className="mx-auto w-full max-w-3xl px-4 pb-24 pt-28 sm:px-5 md:px-8 lg:pt-32"
        >
          <Breadcrumbs items={[{ label: "Compare", href: "/compare" }, { label: entry.term }]} />

          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-primary-text">
            {entry.category} · vs. {entry.vendor}
          </p>
          <h1 className="mb-4 text-4xl font-extrabold tracking-tight text-text sm:text-5xl">
            {entry.term}
          </h1>

          {/* Quotable direct-answer block — the summary most likely to be
              lifted verbatim into an AI Overview or answer-engine citation. */}
          <p className="mb-8 rounded-2xl border border-primary-border/60 bg-primary-dim/40 p-5 text-lg leading-relaxed text-text">
            {entry.shortDefinition}
          </p>

          <article className="article-rich-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ href, children, ...props }) => {
                  const isExternal = typeof href === "string" && href.startsWith("http");
                  return (
                    <a
                      href={href}
                      {...props}
                      target={isExternal ? "_blank" : undefined}
                      rel={isExternal ? "noreferrer" : undefined}
                    >
                      {children}
                    </a>
                  );
                },
              }}
            >
              {entry.content}
            </ReactMarkdown>
          </article>

          {entry.faq.length > 0 && (
            <section className="mt-12 rounded-2xl border border-border bg-bg-secondary/60 p-6">
              <h2 className="mb-4 flex items-center gap-2 text-sm font-bold text-text">
                <HelpCircle className="h-4 w-4 text-primary" />
                Frequently asked
              </h2>
              <div className="space-y-5">
                {entry.faq.map((item) => (
                  <div key={item.question}>
                    <h3 className="mb-1.5 text-sm font-semibold text-text">{item.question}</h3>
                    <p className="text-sm leading-relaxed text-text-muted">{item.answer}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="mt-8 rounded-2xl border border-border bg-bg-secondary/60 p-6">
            <h2 className="mb-4 flex items-center gap-2 text-sm font-bold text-text">
              <BookOpen className="h-4 w-4 text-primary" />
              In the Anter docs
            </h2>
            <ul className="space-y-2">
              {entry.docsRefs.map((ref) => (
                <li key={ref.href}>
                  <a
                    href={ref.href}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-text underline-offset-4 hover:underline"
                  >
                    {ref.label}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </a>
                </li>
              ))}
            </ul>
          </section>

          {related.length > 0 && (
            <section className="mt-8">
              <h2 className="mb-4 text-sm font-bold text-text">Related comparisons</h2>
              <div className="flex flex-wrap gap-2">
                {related.map((c) => (
                  <a
                    key={c.slug}
                    href={`/compare/${c.slug}`}
                    className="rounded-full border border-border bg-bg-secondary/60 px-3.5 py-1.5 text-xs font-medium text-text transition hover:border-primary-border hover:bg-bg-hover"
                  >
                    {c.term}
                  </a>
                ))}
              </div>
            </section>
          )}

          <p className="mt-10 text-xs text-text-faint">
            Last updated{" "}
            {new Intl.DateTimeFormat("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            }).format(new Date(entry.updatedAt))}
          </p>
        </main>

        <SiteFooter />
      </div>
    </>
  );
}
